import React from "react";
import {
  MONTHLY_CHECKOUT_COPY,
  formatEuros,
  type MonthlyReview,
} from "@lemonadetraining/web-shared/utils/commitmentPlans";
import { ClosureDisclosure, Row } from "./ReviewBlockParts";

/**
 * What a rolling membership is, spelled out before it is bought.
 *
 * The commitment panel's sibling, sharing its frame and its rows. Deliberately
 * shorter: with no fixed term there is no whole-term total, no saving against
 * paying monthly, no end date and no ending to describe. Four rows that are
 * true of a month-to-month membership, and the closure disclosure, which is
 * equally true of one.
 */

interface MonthlyReviewBlockProps {
  title: string;
  review: MonthlyReview;
  /** Distinguishes the blocks when a cart holds more than one membership. */
  testIdSuffix: string;
}

const MonthlyReviewBlock = ({ title, review, testIdSuffix }: MonthlyReviewBlockProps) => {
  const headingId = `monthly-review-heading-${testIdSuffix}`;

  return (
    <section
      aria-labelledby={headingId}
      data-testid={`monthly-review-${testIdSuffix}`}
      className="mt-4 rounded-lg border p-4"
    >
      <h3 id={headingId} className="text-sm font-semibold">
        {MONTHLY_CHECKOUT_COPY.reviewHeading}
      </h3>
      <p className="text-muted-foreground mb-2 text-xs">{title}</p>

      <dl className="divide-y">
        <Row
          label={MONTHLY_CHECKOUT_COPY.termLabel}
          value={MONTHLY_CHECKOUT_COPY.termValue}
          testId={`monthly-term-${testIdSuffix}`}
        />

        <Row
          label={MONTHLY_CHECKOUT_COPY.paymentsLabel}
          value={MONTHLY_CHECKOUT_COPY.payments(formatEuros(review.monthlyAmount))}
          testId={`monthly-payments-${testIdSuffix}`}
        />

        <Row
          label={MONTHLY_CHECKOUT_COPY.renewsLabel}
          value={MONTHLY_CHECKOUT_COPY.renewsValue}
          testId={`monthly-renews-${testIdSuffix}`}
        />

        <Row
          label={MONTHLY_CHECKOUT_COPY.cancellingLabel}
          value={MONTHLY_CHECKOUT_COPY.cancellingValue}
          testId={`monthly-cancelling-${testIdSuffix}`}
        />
      </dl>

      {!review.fromFreshConfig && (
        <p
          className="text-muted-foreground mt-2 text-xs"
          data-testid={`monthly-stale-note-${testIdSuffix}`}
        >
          {MONTHLY_CHECKOUT_COPY.staleConfigNote}
        </p>
      )}

      {review.priceChanged && (
        <p
          className="mt-2 text-xs font-medium"
          data-testid={`monthly-price-changed-${testIdSuffix}`}
        >
          {MONTHLY_CHECKOUT_COPY.priceChangedNote}
        </p>
      )}

      <ClosureDisclosure testIdSuffix={`monthly-${testIdSuffix}`} />
    </section>
  );
};

export default MonthlyReviewBlock;
